import { z } from 'zod';
import { zIndicatorResponse } from '@/lib/schema/zod';

/**
 * Runtime validator for imported ObservationRecord JSON.
 *
 * Permissive on optional fields and tolerant of forward drift in the JSON
 * format (unknown keys are stripped by default — no `.strict()`), but
 * rejects outright malformed input so IndexedDB cannot be poisoned by a
 * hand-edited file.
 */

const zItemStatus = z.enum(['untouched', 'in_progress', 'complete']);

const zEnrollmentByAge = z.object({
  m0_5: z.number().int().min(0).default(0),
  m6_11: z.number().int().min(0).default(0),
  m12_17: z.number().int().min(0).default(0),
  m18_23: z.number().int().min(0).default(0),
  m24_35: z.number().int().min(0).default(0),
  y3_5: z.number().int().min(0).default(0),
  y6_8: z.number().int().min(0).default(0),
  y9_12: z.number().int().min(0).default(0),
});

// Every field carries a default so records exported before the scoresheet
// fields landed still import cleanly.
const zMetadata = z.object({
  observerName: z.string().default(''),
  observerEmail: z.string().default(''),
  networkName: z.string().default(''),
  providerName: z.string().default(''),
  providerPerson: z.string().default(''),
  providerLicenseId: z.string().default(''),
  siteName: z.string().default(''),
  siteAddress: z.string().default(''),
  observationDate: z.string().default(''),
  startTime: z.string().default(''),
  endTime: z.string().default(''),
  providersPresent: z.number().int().min(0).default(0),
  childrenEnrolled: z.number().int().min(0).default(0),
  enrollmentByAge: zEnrollmentByAge.default({
    m0_5: 0, m6_11: 0, m12_17: 0, m18_23: 0,
    m24_35: 0, y3_5: 0, y6_8: 0, y9_12: 0,
  }),
  highestAllowedAtOneTime: z.number().int().min(0).default(0),
  childrenPresent: z.number().int().min(0).default(0),
  totalEnrolledPastMonth: z.number().int().min(0).default(0),
  childrenSpecialNeeds: z.number().int().min(0).default(0),
  specialNeedTypes: z.array(z.string()).default([]),
  specialNeedOther: z.string().default(''),
  grossMotorIndoors: z.string().default(''),
  grossMotorOutdoors: z.string().default(''),
  foodPreferences: z.string().default(''),
  foodAllergies: z.string().default(''),
  ageGroupsPresent: z.array(z.string()).default([]),
  adultsPresent: z.number().int().min(0).default(0),
  notes: z.string().default(''),
});

export const zObservationRecord = z.object({
  id: z.string().min(1),
  instrumentId: z.literal('FCCERS-3'),
  schemaVersion: z.literal('1.0.0'),
  status: z.enum(['draft', 'submitted']),
  metadata: zMetadata,
  responses: z.record(z.string(), zIndicatorResponse).default({}),
  itemNa: z.record(z.string(), z.boolean()).default({}),
  itemNotes: z.record(z.string(), z.string()).default({}),
  itemStatus: z.record(z.string(), zItemStatus).default({}),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
  submittedAt: z.number().int().optional(),
  syncedAt: z.number().int().optional(),
});

/**
 * New "wrapped" export format: `{ schemaVersion, exportedAt, record, scores }`.
 * `scores` is trusted output — we don't re-validate it here; the UI recomputes
 * from `record.responses` on load anyway.
 */
const zObservationExport = z.object({
  record: zObservationRecord,
});

export type ImportResult =
  | { ok: true; record: z.infer<typeof zObservationRecord> }
  | { ok: false; error: string };

export function parseObservationImport(raw: unknown): ImportResult {
  // Prefer the wrapped shape; fall back to a bare record for backward
  // compatibility with exports produced before the wrapper landed.
  const wrapped = zObservationExport.safeParse(raw);
  if (wrapped.success) return { ok: true, record: wrapped.data.record };

  const bare = zObservationRecord.safeParse(raw);
  if (bare.success) return { ok: true, record: bare.data };

  // Surface the wrapped-shape errors since that's the canonical format.
  const issues = wrapped.error.issues
    .slice(0, 5)
    .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
    .join('; ');
  return { ok: false, error: `Invalid observation file — ${issues}` };
}
